import { CopyOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Input, Select, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { listPromoCodes, promoErrorMessage } from "../actions/promo_actions";
import ContentLayout from "../components/layout/ContentLayout";
import PromoCodeDrawer from "../components/promo/PromoCodeDrawer";
import PromoCodeFormModal from "../components/promo/PromoCodeFormModal";
import { PERIOD_LABELS, PROMO_STATE_TAGS, describeDuration, promoState, usageLabel } from "../utils/promo";

const activeOptions = [
  { value: "", label: "All codes" },
  { value: "true", label: "Accepting signups" },
  { value: "false", label: "Paused" },
];

const formatDate = (value) =>
  value ? new Intl.DateTimeFormat("en", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value)) : null;

export default function PromoCodes() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["promo-codes", page, limit, search, active],
    queryFn: () =>
      listPromoCodes({ page, limit, ...(search ? { search } : {}), ...(active ? { active } : {}) }),
    keepPreviousData: true,
    retry: false,
  });

  const codes = data?.docs || [];
  const pagination = data?.pagination || {};

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      width: 190,
      render: (code, row) => (
        <div>
          <Typography.Text strong copyable={{ text: code, icon: <CopyOutlined /> }} className="promo-code-cell">
            {code}
          </Typography.Text>
          {row.description && <div className="subscription-secondary">{row.description}</div>}
        </div>
      ),
    },
    {
      title: "Discount",
      width: 230,
      render: (_, row) => (
        <div>
          <strong>{Number(row.percent_off)}% off</strong>
          <div className="subscription-secondary">for {describeDuration(row)}</div>
        </div>
      ),
    },
    {
      title: "Billing",
      dataIndex: "billing_periods",
      width: 150,
      render: (periods = []) => periods.map((period) => <Tag key={period}>{PERIOD_LABELS[period]}</Tag>),
    },
    { title: "Usage", width: 120, render: (_, row) => usageLabel(row) },
    {
      title: "Valid",
      width: 190,
      render: (_, row) => {
        const from = formatDate(row.valid_from);
        const until = formatDate(row.expires_at);
        if (!from && !until) return "Always";
        return [from && `from ${from}`, until && `until ${until}`].filter(Boolean).join(" ");
      },
    },
    {
      title: "Status",
      width: 120,
      render: (_, row) => {
        const tag = PROMO_STATE_TAGS[promoState(row)];
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    { title: "Created by", dataIndex: "created_by", width: 200, render: (value) => value || "—" },
  ];

  return (
    <ContentLayout
      header={
        <h1>
          Promo Codes <span>Sales</span>
        </h1>
      }
      bodyClassName="subscriptions-page promo-codes-page"
    >
      <div className="subscription-toolbar promo-toolbar">
        <Input.Search
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onSearch={(value) => {
            setSearch(String(value || "").trim());
            setPage(1);
          }}
          placeholder="Search code, note or creator"
          enterButton={<SearchOutlined />}
          allowClear
        />
        <Select
          value={active}
          options={activeOptions}
          onChange={(value) => {
            setActive(value);
            setPage(1);
          }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          New promo code
        </Button>
      </div>

      {isError && <Alert type="error" showIcon message="Failed to load promo codes" description={promoErrorMessage(error)} />}

      <Table
        columns={columns}
        dataSource={codes}
        loading={isLoading}
        rowKey="id"
        onRow={(row) => ({ onClick: () => setSelectedId(row.id), className: "subscription-row" })}
        locale={{ emptyText: "No promo codes yet. Create one for the sales team." }}
        pagination={{
          current: pagination.page || page,
          pageSize: pagination.limit || limit,
          total: pagination.totalItems || 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total) => `${total} promo codes`,
          onChange: (nextPage, nextLimit) => {
            setPage(nextLimit !== limit ? 1 : nextPage);
            setLimit(nextLimit);
          },
        }}
        scroll={{ x: 1200 }}
      />

      <PromoCodeFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(promo) => {
          refetch();
          setSelectedId(promo.id);
        }}
      />
      <PromoCodeDrawer promoId={selectedId} onClose={() => setSelectedId(null)} onUpdated={refetch} />
    </ContentLayout>
  );
}
